import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Plays a short "pop out" exit animation before an item is actually removed.
 *
 * The caller marks an item as leaving (by a stable key); the hook keeps it in
 * the `leaving` set for `durationMs`, then runs the caller's commit (the real
 * state removal) and clears the mark. Rendering code reads `isLeaving(key)` to
 * apply the shrink/fade styles while the item is still mounted — the block
 * "floep"s away instead of vanishing instantly.
 */
export function useRemovalAnimation(durationMs = 160) {
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const pending = useRef(
    new Map<string, { timer: ReturnType<typeof setTimeout>; commit: () => void }>(),
  );

  const remove = useCallback(
    (key: string, commit: () => void) => {
      // Ignore a double-trigger while the same key is already animating out.
      if (pending.current.has(key)) return;
      setLeaving((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      const timer = setTimeout(() => {
        pending.current.delete(key);
        commit();
        setLeaving((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, durationMs);
      pending.current.set(key, { timer, commit });
    },
    [durationMs],
  );

  const isLeaving = useCallback((key: string) => leaving.has(key), [leaving]);

  // True while any item is animating out — lets callers skip per-item key work
  // in the common case where nothing is being removed.
  const hasLeaving = leaving.size > 0;

  // On unmount, FLUSH any not-yet-committed removals rather than dropping them:
  // the animation is cut short but the delete still lands in the parent's state
  // (the commit runs). Otherwise a delete followed by a fast tab/date switch
  // would resurrect the block, even though the toast already said "deleted".
  useEffect(() => {
    const map = pending.current;
    return () => {
      map.forEach(({ timer, commit }) => {
        clearTimeout(timer);
        commit();
      });
      map.clear();
    };
  }, []);

  return { remove, isLeaving, hasLeaving };
}
